import { useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiGet, apiPost, apiPatch } from 'api'
import AuthContext from 'context/auth-context'

// Municipal self-service user administration (requires is_user_admin).
// All endpoints are scoped server-side to the caller's municipality.

const USERS_KEY = ['municipality-admin-users']

const getUsers = async (token) => apiGet({ url: 'municipality_admin/users', token })

const createUser = async (token, params) =>
  apiPost({ url: 'municipality_admin/users', token, objectName: 'user', params })

const updateUser = async (token, userId, params) =>
  apiPatch({ url: `municipality_admin/users/${userId}`, token, objectName: 'user', params })

const blockUser = async (token, userId) =>
  apiPost({ url: `municipality_admin/users/${userId}/block`, token, objectName: 'user', params: {} })

const unblockUser = async (token, userId) =>
  apiPost({ url: `municipality_admin/users/${userId}/unblock`, token, objectName: 'user', params: {} })

const sendPasswordReset = async (token, userId) =>
  apiPost({ url: `municipality_admin/users/${userId}/password_reset`, token, objectName: 'user', params: {} })

export const useMunicipalityAdminUsers = ({ options } = {}) => {
  const token = useContext(AuthContext)
  return useQuery(USERS_KEY, () => getUsers(token), options)
}

const useInvalidatingMutation = (mutationFn, options) => {
  const queryClient = useQueryClient()
  return useMutation(mutationFn, {
    ...options,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(USERS_KEY)
      options?.onSuccess?.(data, variables)
    }
  })
}

export const useCreateMunicipalityAdminUser = ({ options } = {}) => {
  const token = useContext(AuthContext)
  return useInvalidatingMutation((params) => createUser(token, params), options)
}

export const useUpdateMunicipalityAdminUser = ({ options } = {}) => {
  const token = useContext(AuthContext)
  return useInvalidatingMutation(({ userId, params }) => updateUser(token, userId, params), options)
}

export const useBlockMunicipalityAdminUser = ({ options } = {}) => {
  const token = useContext(AuthContext)
  return useInvalidatingMutation((userId) => blockUser(token, userId), options)
}

export const useUnblockMunicipalityAdminUser = ({ options } = {}) => {
  const token = useContext(AuthContext)
  return useInvalidatingMutation((userId) => unblockUser(token, userId), options)
}

export const useSendMunicipalityAdminPasswordReset = ({ options } = {}) => {
  const token = useContext(AuthContext)
  return useMutation((userId) => sendPasswordReset(token, userId), options)
}
